import { useEffect, useRef } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { whatsappGatewaysApi, type EstadoGateway, type GatewayDetalhe } from '@/api/whatsappGateways'
import { whatsapiKbApi } from '@/api/whatsapi'
import { raizDaOrg, whatsapiOrgApi } from '@/api/whatsapiOrg'
import { useAuthStore } from '@/store/auth'
import { mensagemDeErro } from '@/components/admin/whatsappGateways/mensagens'

// Provisionar, atualizar e remover são jobs que andam um passo por tick do worker
// (20 s, `worker.INTERVALO_S`): enquanto houver um em curso, a tela relê no mesmo ritmo.
export const EM_CURSO: ReadonlySet<EstadoGateway> = new Set<EstadoGateway>(['provisionando', 'atualizando', 'removendo'])
const TICK_DO_WORKER_MS = 20_000

export function useOrganizacoesWagw() {
  const userId = useAuthStore((s) => s.user?.id)
  return useQuery({
    queryKey: ['wagw-organizacoes', userId],
    queryFn: () => whatsappGatewaysApi.organizacoes(),
    enabled: userId != null,
    staleTime: 5 * 60_000,
  })
}

export function useGateways(orgId: number | null) {
  return useQuery({
    queryKey: ['wagw-gateways', orgId],
    queryFn: () => whatsappGatewaysApi.listar(orgId as number),
    enabled: orgId != null,
    refetchInterval: (q) => ((q.state.data?.items ?? []).some((g) => EM_CURSO.has(g.status)) ? TICK_DO_WORKER_MS : false),
  })
}

export function useGateway(orgId: number, gwId: string) {
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['wagw-gateway', orgId, gwId],
    queryFn: () => whatsappGatewaysApi.obter(orgId, gwId),
    refetchInterval: (q) => (q.state.data && EM_CURSO.has(q.state.data.status) ? TICK_DO_WORKER_MS : false),
  })
  // Spec 2026-10-15 §4.5: quando o job sai de curso, a última linha da trilha (o
  // `provisioned`/`version_updated`/`*_failed`) só aparece se a trilha for relida — pela
  // transição observada aqui, não por mais um timer.
  const status = q.data?.status
  const antes = useRef(status)
  useEffect(() => {
    const saiuDeCurso = antes.current != null && EM_CURSO.has(antes.current) && status != null && !EM_CURSO.has(status)
    antes.current = status
    if (saiuDeCurso) {
      void qc.invalidateQueries({ queryKey: ['wagw-trilha', orgId, gwId] })
      void qc.invalidateQueries({ queryKey: ['wagw-operacoes', orgId, gwId] })
    }
  }, [status, qc, orgId, gwId])
  return q
}

function emCursoNoCache(qc: QueryClient, orgId: number, gwId: string): boolean {
  const gw = qc.getQueryData<GatewayDetalhe>(['wagw-gateway', orgId, gwId])
  return gw != null && EM_CURSO.has(gw.status)
}

export function useTrilha(orgId: number, gwId: string) {
  const qc = useQueryClient()
  return useQuery({
    queryKey: ['wagw-trilha', orgId, gwId],
    queryFn: () => whatsappGatewaysApi.trilha(orgId, gwId),
    // Em curso, os passos gravam na trilha a cada tick: mesmo ritmo do detalhe.
    refetchInterval: () => (emCursoNoCache(qc, orgId, gwId) ? TICK_DO_WORKER_MS : false),
  })
}

/** Aba "Operações": os jobs do gateway, mais recente primeiro, página a página por `proximo`. */
export function useOperacoes(orgId: number, gwId: string) {
  const qc = useQueryClient()
  return useInfiniteQuery({
    queryKey: ['wagw-operacoes', orgId, gwId],
    queryFn: ({ pageParam }) => whatsappGatewaysApi.operacoes(orgId, gwId, pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (ultima) => ultima.proximo,
    // O job em curso anda um passo por tick: a situação e a duração dele mudam no mesmo ritmo.
    refetchInterval: () => (emCursoNoCache(qc, orgId, gwId) ? TICK_DO_WORKER_MS : false),
  })
}

/** Passos de um job da aba "Operações". `jobId` nulo não busca (linha recolhida). */
export function usePassosDoJob(orgId: number, gwId: string, jobId: number | null, emCurso: boolean) {
  return useQuery({
    queryKey: ['wagw-passos-do-job', orgId, gwId, jobId],
    queryFn: () => whatsappGatewaysApi.passosDoJob(orgId, gwId, jobId as number),
    enabled: jobId != null,
    refetchInterval: emCurso ? TICK_DO_WORKER_MS : false,
  })
}

// O servidor guarda a conferência por 30 s; reler mais rápido só devolveria a mesma.
const CACHE_DA_SAUDE_MS = 30_000

export function useSaudeDoGateway(orgId: number, gwId: string) {
  return useQuery({
    queryKey: ['wagw-saude', orgId, gwId],
    queryFn: () => whatsappGatewaysApi.saude(orgId, gwId),
    refetchInterval: CACHE_DA_SAUDE_MS,
  })
}

// Transbordo novo chega sem a tela saber: relê a cada 15 s (como Mensagens) e ao focar a tela.
// A lista não tem cache no servidor, então cada leitura já traz a pausa nova (#26135).
const RELEITURA_DAS_CONVERSAS_MS = 15_000

export function useConversasEmPausa(orgId: number, gwId: string) {
  return useQuery({
    queryKey: ['wagw-bot-conversas', orgId, gwId],
    queryFn: () => whatsappGatewaysApi.conversasEmPausa(orgId, gwId),
    refetchInterval: RELEITURA_DAS_CONVERSAS_MS,
    refetchOnWindowFocus: true,
  })
}

/** Devolve a conversa ao bot pela `devolver` de WhatsApi › Conversas (spec 2026-11-02
 * conversas §5.6; a `liberar` da precursora saiu). Sucesso ou 409 (`conversa_nao_pausada`): a
 * lista está velha e recarrega; o resumo do bot no "Estado atual" e a lista de Conversas também.
 * No sucesso o "Estado atual" é relido com `atualizar` (#26137): o cache de 30 s do servidor,
 * por réplica, ainda contaria a conversa em atendimento humano. */
export function useDevolverConversa(orgId: number, gwId: string) {
  const qc = useQueryClient()
  const chaveDaSaude = ['wagw-saude', orgId, gwId]
  const recarregar = () => {
    void qc.invalidateQueries({ queryKey: ['wagw-bot-conversas', orgId, gwId] })
    void qc.invalidateQueries({ queryKey: ['whatsapi-conversas', orgId] })
  }
  const relerSaude = async () => {
    // A leitura periódica em voo traria o guardado e sobrescreveria a releitura.
    await qc.cancelQueries({ queryKey: chaveDaSaude })
    try {
      qc.setQueryData(chaveDaSaude, await whatsappGatewaysApi.saude(orgId, gwId, true))
    } catch {
      void qc.invalidateQueries({ queryKey: chaveDaSaude })
    }
  }
  return useMutation({
    mutationFn: (ref: string) => whatsapiOrgApi.devolverConversa(orgId, gwId, ref),
    onSuccess: () => {
      recarregar()
      void relerSaude()
      void qc.invalidateQueries({ queryKey: ['wagw-trilha', orgId, gwId] })
    },
    onError: (e) => {
      recarregar()
      void qc.invalidateQueries({ queryKey: chaveDaSaude })
      avisarErro(e)
    },
  })
}

export function useChavesDaOrg(orgId: number) {
  return useQuery({ queryKey: ['wagw-chaves', orgId], queryFn: () => whatsappGatewaysApi.chaves(orgId), staleTime: 60_000 })
}

/**
 * Arquivos da Base de Conhecimento de uma chave (rota do WhatsApi da organização, que pede
 * `whatsapi:read` nela): a confirmação de excluir diz quantos vão junto com a chave.
 * `keyId` nulo não busca. Sem retry: falha (ou falta de permissão) vira o aviso genérico.
 */
export function useArquivosDaBaseDaChave(orgId: number, keyId: string | null) {
  return useQuery({
    queryKey: ['wagw-kb-da-chave', orgId, keyId],
    queryFn: () => whatsapiKbApi.list(raizDaOrg(orgId), keyId ?? ''),
    enabled: keyId != null,
    retry: false,
    staleTime: 0,
  })
}

function invalidar(qc: QueryClient, orgId: number, gwId?: string) {
  void qc.invalidateQueries({ queryKey: ['wagw-gateways', orgId] })
  if (gwId) {
    void qc.invalidateQueries({ queryKey: ['wagw-gateway', orgId, gwId] })
    void qc.invalidateQueries({ queryKey: ['wagw-trilha', orgId, gwId] })
    void qc.invalidateQueries({ queryKey: ['wagw-operacoes', orgId, gwId] })
  }
}

function avisarErro(e: unknown) {
  notifications.show({ color: 'red', title: 'Não foi possível concluir', message: mensagemDeErro(e) })
}

export function useCriarGateway(orgId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (name: string) => whatsappGatewaysApi.criar(orgId, name),
    onSuccess: () => invalidar(qc, orgId),
    onError: avisarErro,
  })
}

export function useRenomearGateway(orgId: number, gwId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (name: string) => whatsappGatewaysApi.renomear(orgId, gwId, name),
    onSuccess: () => invalidar(qc, orgId, gwId),
    onError: avisarErro,
  })
}

export function useRemoverGateway(orgId: number, gwId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => whatsappGatewaysApi.remover(orgId, gwId),
    onSuccess: () => invalidar(qc, orgId, gwId),
    onError: (e) => {
      // 409 `gateway_com_chaves`: a lista de chaves da tela pode estar velha (staleTime).
      void qc.invalidateQueries({ queryKey: ['wagw-chaves', orgId] })
      avisarErro(e)
    },
  })
}

export function useReiniciarGateway(orgId: number, gwId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => whatsappGatewaysApi.reiniciar(orgId, gwId),
    onSuccess: () => {
      invalidar(qc, orgId, gwId)
      notifications.show({ color: 'green', title: 'Reinício pedido', message: 'O ECS sobe uma tarefa nova com a mesma versão do gateway.' })
    },
    onError: avisarErro,
  })
}

export function useAtualizarGateway(orgId: number, gwId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => whatsappGatewaysApi.atualizar(orgId, gwId),
    onSuccess: () => {
      invalidar(qc, orgId, gwId)
      notifications.show({ color: 'green', title: 'Atualização pedida', message: 'O gateway sobe na versão corrente; acompanhe o andamento aqui.' })
    },
    onError: avisarErro,
  })
}

export function useTentarDeNovo(orgId: number, gwId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => whatsappGatewaysApi.tentarDeNovo(orgId, gwId),
    onSuccess: () => {
      invalidar(qc, orgId, gwId)
      notifications.show({ color: 'green', title: 'Provisionamento retomado', message: 'O gateway recomeça do passo que falhou; acompanhe aqui.' })
    },
    onError: avisarErro,
  })
}

export function useDesparear(orgId: number, gwId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => whatsappGatewaysApi.desparear(orgId, gwId),
    onSuccess: () => invalidar(qc, orgId, gwId),
    onError: avisarErro,
  })
}

function useMudarChave<T>(orgId: number, gwId: string, fn: (keyId: string) => Promise<T>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wagw-chaves', orgId] })
      invalidar(qc, orgId, gwId)
    },
    onError: avisarErro,
  })
}

export function useApontarChave(orgId: number, gwId: string) {
  return useMudarChave(orgId, gwId, (keyId) => whatsappGatewaysApi.apontarChave(orgId, gwId, keyId))
}

export function useDesapontarChave(orgId: number, gwId: string) {
  return useMudarChave(orgId, gwId, (keyId) => whatsappGatewaysApi.desapontarChave(orgId, gwId, keyId))
}

// Ciclo de vida (spec 2026-10-16). A key em texto de criar/rotacionar fica só no
// `data` da mutação — a tela a mostra num modal e chama `reset()` ao fechar.
export function useCriarChave(orgId: number, gwId: string) {
  return useMudarChave(orgId, gwId, (name) => whatsappGatewaysApi.criarChave(orgId, gwId, name))
}

export function useRotacionarChave(orgId: number, gwId: string) {
  return useMudarChave(orgId, gwId, (keyId) => whatsappGatewaysApi.rotacionarChave(orgId, gwId, keyId))
}

export function useAtivarChave(orgId: number, gwId: string) {
  return useMudarChave(orgId, gwId, (keyId) => whatsappGatewaysApi.ativarChave(orgId, gwId, keyId))
}

export function useDesativarChave(orgId: number, gwId: string) {
  return useMudarChave(orgId, gwId, (keyId) => whatsappGatewaysApi.desativarChave(orgId, gwId, keyId))
}

export function useExcluirChave(orgId: number, gwId: string) {
  return useMudarChave(orgId, gwId, (keyId) => whatsappGatewaysApi.excluirChave(orgId, gwId, keyId))
}
